
<script>
import Card from '@vvg/forum/vue/components/card.vue'
import {Link} from "@inertiajs/vue3";
import Permissions from '@vvg/forum/js/mixins/permissions.js'
import Ajax from '@vvg/forum/js/mixins/ajax.js'
import Scroll from '@vvg/forum/js/mixins/scroll.js'
import Pagination from '@vvg/forum/vue/components/pagination.vue'

export default {
	components: {Card, Link, Pagination},
	mixins: [Ajax, Permissions, Scroll],
	props: {
		forum: {
			type: Object,
			required: true
		}
	},

	computed: {
		paginationData() {
			return {
				forum_id: this.$props.forum.id,
				search: this.search,
			};
		},
		// Unique key to remember which onderwerp (and page) was opened from this list
		scrollKey() {
			return 'topics:' + this.$props.forum.id;
		},
	},
	data() {
		return {
			topics: [],
			loading: 1,
			search: '',
			initialPage: 1,
			pendingScrollId: null,
		};
	},
	created() {
		const target = this.readForumScroll(this.scrollKey);
		if (target) {
			this.initialPage = target.page || 1;
			this.pendingScrollId = target.targetId || null;
		}
	},
	methods: {
		onItemsLoaded(data) {
			this.topics = data.items;
			this.$nextTick(() => this.applyScroll());
		},

		applyScroll() {
			if (this.pendingScrollId && this.scrollToId(this.pendingScrollId)) {
				this.pendingScrollId = null;
				this.clearForumScroll(this.scrollKey);
				return;
			}
			this.pendingScrollId = null;
			this.clearForumScroll(this.scrollKey);
			this.scrollToForum();
		},
		openTopic(topic) {
			this.rememberForumScroll(this.scrollKey, {
				page: this.$refs.pagination ? this.$refs.pagination.page : 1,
				targetId: 'topic-' + topic.id,
			});
		},
		deleteTopic(index, id) {
			if (!confirm('Weet u zeker dat u dit onderwerp wilt verwijderen?')) return;
			this.Ajax(this.$page.props.forum_url + 'delete_topic/?topic_id' + id, 'POST', {topic_id: id}).then(response => {
				this.showToast(response.message, 'success');
				this.topics.splice(index, 1);
			});
		},
	},
};
</script>

<template>
  <div class="container-xl py-4">

    <div class="topic-topbar d-flex flex-column flex-md-row justify-content-start justify-content-md-between align-items-md-center mb-4">
      <nav class="topic-breadcrumb" aria-label="Navigatie">
        <Link :href="$page.props.forum_url" class="topic-breadcrumb__link">Forum</Link>
        <i class="fa fa-chevron-right topic-breadcrumb__sep"></i>
        <span>{{ forum.title }}</span>
        <i class="fa fa-chevron-right topic-breadcrumb__sep"></i>
        <span class="text-muted">Onderwerpen</span>
      </nav>
      <Link
          v-if="isAuthenticated()"
          :href="$page.props.forum_url + forum.id + '/onderwerpen/aanmaken/'"
          class="btn btn-dark btn-sm d-flex align-items-center gap-2"
      >
        <i class="fa fa-plus" style="padding-right: 10px;"></i> Aanmaken
      </Link>
    </div>

    <div v-if="forum.overview_image_url || forum.intro" class="forum-intro mb-4">
      <img
          v-if="forum.overview_image_url"
          :src="forum.overview_image_url"
          :alt="forum.title"
          class="forum-intro__image"
      />
      <div v-if="forum.intro" class="forum-intro__content text-wrapper  mb-0" v-html="forum.intro"></div>
    </div>

    <div class="topic-search mb-3">
      <i class="fa fa-search topic-search__icon"></i>
      <input
          v-model="search"
          type="text"
          class="form-control topic-search__input"
          placeholder="Zoek op onderwerp..."
          @keyup="$refs.pagination.changePage(1)"
      />
    </div>

    <div class="topic-list">
      <div
          v-for="(topic, index) in topics"
          :key="topic.id"
          :id="'topic-' + topic.id"
          class="topic-row"
          @click="openTopic(topic)"
      >
        <Link
            :href="$page.props.forum_url + forum.id + '/onderwerpen/' + topic.id + '/'"
            class="topic-row__left"
        >
          <div class="topic-thumb">
            <img
                v-if="topic.overview_image"
                :src="topic.overview_image.file_location"
                :alt="topic.title"
            />
            <i v-else class="fa fa-file-text-o text-muted"></i>
          </div>
          <div class="topic-body">
            <div class="topic-title" :class="{ 'is-unread': topic.is_unread }">
              {{ topic.title }}
            </div>
            <div class="topic-desc text-wrapper" :class="{ 'is-unread': topic.is_unread }" v-html="topic.description"></div>
            <div class="topic-replies" :class="{ 'is-unread': topic.is_unread }">
              <i class="fa fa-comment"></i>
              {{ topic.amount_reactions ?? 0 }} {{ (topic.amount_reactions ?? 0) === 1 ? 'antwoord' : 'antwoorden' }}
            </div>
          </div>
        </Link>

        <div class="topic-last">
          <template v-if="topic.last_reaction">
            <div class="last-user" :class="{ 'is-unread': topic.is_unread }">
              <Link
                v-if="topic.last_reaction.user && $page.props.auth.user"
                :href="$page.props.forum_url + 'profiel/' + topic.last_reaction.user.id + '/'"
                class="last-user-link"
                @click.stop
              >{{ topic.last_reaction.user.username }}</Link>
              <span v-else-if="topic.last_reaction.user">{{ topic.last_reaction.user.username }}</span>
              <span v-else>—</span>
            </div>
            <div class="last-time" :class="{ 'is-unread': topic.is_unread }">
              {{ topic.last_reaction.created_at_formatted }}
            </div>
          </template>
          <span v-else class="no-reactions">Nog geen reacties</span>
        </div>

        <div class="topic-row__actions">
          <Link
              v-if="isSameUser(topic.user_id) || hasRole('Administrator')"
              :href="$page.props.forum_url + forum.id + '/onderwerpen/' + topic.id + '/bewerken/'"
              class="act-btn"
              title="Bewerken"
          >
            <i class="fa fa-edit"></i>
          </Link>
          <button
              v-if="isSameUser(topic.user_id) || hasRole('Administrator')"
              @click="deleteTopic(index, topic.id)"
              class="act-btn act-btn--danger"
              title="Verwijderen"
          >
            <i class="fa fa-trash"></i>
          </button>
          <Link
              :href="$page.props.forum_url + forum.id + '/onderwerpen/' + topic.id + '/'"
              class="act-btn"
              title="Bekijken"
          >
            <i class="fa fa-eye"></i>
          </Link>
        </div>
      </div>

      <div v-if="topics.length === 0" class="topic-empty">
        Geen onderwerpen gevonden
      </div>
    </div>

    <div class="mt-3">
      <Pagination
          ref="pagination"
          :per-page="10"
          :current-page="initialPage"
          :route="$page.props.forum_url + 'retrieve_topics/'"
          :ajax-data="paginationData"
          @update:items="onItemsLoaded"
          @update:loading="(state) => loading = state"
      />
    </div>

  </div>
</template>

<style scoped>
.forum-intro {
	background: #f8f9fa;
	border-left: 4px solid #343a40;
	padding: 1rem 1.25rem;
	border-radius: 0 6px 6px 0;
	color: #495057;
	display: flex;
	flex-direction: row;
	align-items: center;

	gap: 20px;
}

.forum-intro__image {
	display: block;
	width: 100%;
	max-width: 160px;
	height: auto;
	border-radius: 4px;
	margin-bottom: 0.75rem;
}


.forum-intro__content :deep(> :first-child) {
	margin-top: 0;
}

.forum-intro__content :deep(> :last-child) {
	margin-bottom: 0;
}

.topic-desc {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.topic-desc :deep(*) {

	font-size: inherit;
	font-weight: inherit;
	line-height: inherit;
}

.last-user-link {
	color: inherit;
	text-decoration: none;
}

.last-user-link:hover {
	text-decoration: underline;
}

.topic-grid {
	display: grid;
	grid-template-columns: 1fr 180px 100px;
	align-items: center;
	gap: 0 16px;
}

.topic-row:hover {
	background: #f8f9fa;
}

.topic-thumbnail {
	width: 88px;
	aspect-ratio: 1;
}

.topic-last-title {
	max-width: 160px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.topic-description {
	overflow: hidden;
	max-width: 480px;
}
</style>